import axios from "axios";
import config from "../../config";

export const QUERY_STATUSES = ["Pending", "Approved", "Creating Task", "Task Created"];
export const QUERY_ROUTE = "/client-query-management";

export const isClientQueryStaff = (user) => {
  const list = user?.roleNames ?? user?.RoleNames ?? user?.normalizedRoles;
  const roles = Array.isArray(list) && list.length ? list : [user?.roleName ?? user?.RoleName ?? user?.role];
  const normalized = (Array.isArray(roles) ? roles : []).filter(Boolean).map((role) =>
    String(role).trim().toLowerCase().replace(/[_-]/g, " ").replace(/\s+/g, " ")
  );
  return normalized.length > 0 && !normalized.some((role) => role === "customer" || role === "customer user");
};

export const normalizeClientQuery = (item = {}) => ({
  id: item.id ?? item.Id,
  queryTitle: String(item.queryTitle ?? item.QueryTitle ?? ""),
  queryDescription: String(item.queryDescription ?? item.QueryDescription ?? ""),
  clientId: String(item.clientId ?? item.ClientId ?? ""),
  orgName: String(item.orgName ?? item.OrgName ?? ""),
  campusName: String(item.campusName ?? item.CampusName ?? ""),
  teacherCode: String(item.teacherCode ?? item.TeacherCode ?? ""),
  createdBy: String(item.createdBy ?? item.CreatedBy ?? ""),
  createdAt: item.createdAt ?? item.CreatedAt ?? "",
  status: item.status ?? item.Status ?? "Pending",
  ticketId: item.ticketId ?? item.TicketId ?? null,
  files: (Array.isArray(item.files ?? item.Files) ? item.files ?? item.Files : []).map((file) => ({
    id: file.id ?? file.Id,
    fileName: file.fileName ?? file.FileName ?? "Attachment",
    fileUrl: file.fileUrl ?? file.FileUrl ?? "",
    fileSize: file.fileSize ?? file.FileSize ?? 0,
    contentType: file.contentType ?? file.ContentType ?? "",
  })),
});

const request = async (method, endpoint, options = {}) => {
  if (!config.baseurl) throw new Error("The CRM API address is not configured.");
  try {
    const token = window.localStorage.getItem("authtoken");
    return await axios.request({
      ...options,
      method,
      url: `${String(config.baseurl).replace(/\/+$/, "")}/${endpoint}`,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  } catch (error) {
    if (axios.isCancel(error) || error.code === "ERR_CANCELED") throw error;
    const status = error.response?.status;
    const data = error.response?.data;
    const validation = Object.values(data?.errors || {}).flat().filter(Boolean).join(" ");
    const message = status === 401 ? "Your session has expired. Please sign in again."
      : status === 403 ? "Your account does not have permission to manage client queries."
        : data?.message || validation || data?.title || "The request failed. Please try again.";
    const failure = new Error(message);
    failure.status = status;
    throw failure;
  }
};

const headerNumber = (headers, name) => {
  const value = headers?.[name];
  if (value === undefined || value === null || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : null;
};

export const getClientQueries = async ({ clientId = "", searchText = "", status = "", page = 1, pageSize = 20, signal } = {}) => {
  const response = await request("GET", "ClientQueryManagement/get", {
    signal,
    params: { clientId: clientId.trim() || undefined, searchText: searchText.trim() || undefined, status: status || undefined, page, pageSize },
  });
  if (!Array.isArray(response.data)) throw new Error("The query list returned an unexpected response.");
  let summary = {};
  try { summary = JSON.parse(response.headers?.["x-query-summary"] || "{}"); } catch { /* Older APIs may omit summary metadata. */ }
  const items = response.data.map(normalizeClientQuery);
  const totalCount = headerNumber(response.headers, "x-total-count");
  const totalPages = headerNumber(response.headers, "x-total-pages");
  return { items, totalCount, totalPages, summary, hasNextPage: totalPages !== null ? page < totalPages : items.length === pageSize };
};

export const getClientQuery = async (id, signal) => normalizeClientQuery(
  (await request("GET", `ClientQueryManagement/getbyid/${encodeURIComponent(id)}`, { signal })).data
);

export const approveClientQuery = async (id) => normalizeClientQuery(
  (await request("POST", `ClientQueryManagement/approve/${encodeURIComponent(id)}`)).data
);

export const makeClientQueryTask = async (id, { ticketId, assignedEmployeeId }) => {
  if (![ticketId, assignedEmployeeId].every((value) => Number.isSafeInteger(Number(value)) && Number(value) > 0))
    throw new Error("Choose a ticket and an employee before creating the task.");
  const response = await request("POST", `ClientQueryManagement/make-task/${encodeURIComponent(id)}`, {
    data: { ticketId: Number(ticketId), assignedEmployeeId: Number(assignedEmployeeId) },
  });
  const query = response.data?.query ?? response.data?.Query;
  const task = response.data?.task ?? response.data?.Task;
  if (!query || !task) throw new Error("The task response was incomplete. Refresh the query before trying again.");
  return { query: normalizeClientQuery(query), taskId: task.id ?? task.Id };
};

export const getQueryEmployees = async (signal) => {
  const response = await request("GET", "Employee/getlist", { signal });
  if (!Array.isArray(response.data)) throw new Error("Employees could not be loaded.");
  return response.data.map((item) => ({
    id: item.id ?? item.Id,
    userId: String(item.userID ?? item.userId ?? item.UserID ?? item.UserId ?? "").trim(),
    name: item.fullName ?? item.FullName ?? item.name ?? item.Name ?? "Employee",
    code: item.employeeCode ?? item.EmployeeCode ?? "",
    isDelete: item.isDelete ?? item.IsDelete ?? 0,
  })).filter((item) => Number(item.id) > 0 && item.userId && Number(item.isDelete) === 0);
};

export const getQueryTickets = async ({ searchText = "", page = 1, signal } = {}) => {
  const response = await request("POST", "Ticket/getpaged", {
    signal,
    data: { pageNumber: page, pageSize: 20, searchText, status: "", organizationId: null, customerId: null, sortDirection: "DESC" },
  });
  const source = response.data?.items ?? response.data?.Items;
  if (!Array.isArray(source)) throw new Error("Tickets could not be loaded.");
  return {
    items: source.map((item) => ({
      id: item.id ?? item.Id,
      number: item.ticketNumber ?? item.TicketNumber ?? "",
      subject: item.subject ?? item.Subject ?? "Untitled ticket",
      customerName: item.customerName ?? item.CustomerName ?? "",
    })),
    hasNextPage: Boolean(response.data.hasNextPage ?? response.data.HasNextPage),
  };
};
